import React, { useEffect } from 'react';
import { Container } from 'reactstrap';
import { Helmet } from 'react-helmet';
import { observer } from 'mobx-react';
import { Link, useParams } from 'react-router-dom';
import styled from 'styled-components';
import {
    Block,
    FlexDiv,
    Heading3,
    palette,
    TextDefault,
    TextTiny,
} from '@awarego-ui/components';
import HeaderSection from '../../components/header/HeaderSection';
import SubHeaderText from '../../components/header/SubHeaderText';
import SubHeaderComponentsList from '../../components/header/SubHeaderComponentsList';
import { useStores } from '../../hooks/use-stores';
import AwarePhishSubNav from './AwarePhishSubNav';

const Metrics = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
    gap: 12px;
    margin: 12px 0 20px;
`;

const Metric = styled(Block)`
    padding: 14px;
`;

const MetricLabel = styled.div`
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    color: ${palette.graphite.charcoal};
`;

const MetricValue = styled.div`
    font-size: 22px;
    font-weight: 700;
    margin-top: 4px;
`;

const Table = styled.table`
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
    th {
        text-align: left;
        font-size: 10px;
        font-weight: 600;
        text-transform: uppercase;
        color: ${palette.graphite.charcoal};
        padding: 8px 10px;
        border-bottom: 1px solid ${palette.graphite.silver};
    }
    td {
        padding: 10px;
        border-bottom: 1px solid ${palette.graphite.cloud};
    }
`;

function AwarePhishCampaignDetail() {
    const { campaignId } = useParams();
    const { store, awarePhishStore } = useStores();
    const { currentCompany } = store;
    const { campaign, loadingCampaign } = awarePhishStore;

    useEffect(() => {
        if (currentCompany && currentCompany.company_id && campaignId) {
            awarePhishStore.loadCampaign(
                currentCompany.company_id,
                campaignId
            );
        }
    }, [currentCompany && currentCompany.company_id, campaignId]);

    return (
        <Container className="manager-content">
            <Helmet>
                <title>
                    {(campaign && campaign.name) || 'Simulation'} · AwarePHISH
                </title>
            </Helmet>
            <HeaderSection
                heading={(campaign && campaign.name) || 'Simulation'}
                subHeadings={
                    <SubHeaderComponentsList>
                        <SubHeaderText
                            text={
                                campaign
                                    ? `Status: ${campaign.status || '—'} · Provider: ${
                                          campaign.provider || 'gophish'
                                      }`
                                    : 'Loading…'
                            }
                        />
                    </SubHeaderComponentsList>
                }
                buttons={
                    <Link
                        to="/aware-phish/campaigns"
                        style={{ fontSize: 13 }}
                    >
                        ← Back to simulations
                    </Link>
                }
            />
            <AwarePhishSubNav active="campaigns" />

            {loadingCampaign || !campaign ? (
                <TextDefault lighter>Loading…</TextDefault>
            ) : (
                <>
                    <Metrics>
                        <Metric>
                            <MetricLabel>Sent</MetricLabel>
                            <MetricValue>{campaign.sent}</MetricValue>
                        </Metric>
                        <Metric>
                            <MetricLabel>Opened</MetricLabel>
                            <MetricValue>{campaign.opened}</MetricValue>
                        </Metric>
                        <Metric>
                            <MetricLabel>Clicked</MetricLabel>
                            <MetricValue>{campaign.clicked}</MetricValue>
                        </Metric>
                        <Metric>
                            <MetricLabel>Submitted</MetricLabel>
                            <MetricValue>{campaign.submitted}</MetricValue>
                        </Metric>
                        <Metric>
                            <MetricLabel>Reported</MetricLabel>
                            <MetricValue>{campaign.reported}</MetricValue>
                        </Metric>
                    </Metrics>

                    <Block padding="16" column>
                        <Heading3>Per-person results</Heading3>
                        <TextTiny lighter style={{ marginBottom: 12 }}>
                            Named results (company attribution mode). Aggregates
                            only can be enforced later in AwareGO 2.0.
                        </TextTiny>
                        {!(campaign.results && campaign.results.length) ? (
                            <TextDefault lighter>
                                No events yet — waiting for GoPhish webhooks.
                            </TextDefault>
                        ) : (
                            <Table>
                                <thead>
                                    <tr>
                                        <th>Email</th>
                                        <th>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {campaign.results.map((r) => (
                                        <tr key={r.email}>
                                            <td>{r.email}</td>
                                            <td>{r.status}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        )}
                    </Block>

                    <FlexDiv style={{ marginTop: 8 }}>
                        <TextTiny lighter>
                            Launch:{' '}
                            {campaign.launchAt
                                ? new Date(campaign.launchAt).toLocaleString()
                                : '—'}{' '}
                            · External id: {campaign.externalId || '—'}
                        </TextTiny>
                    </FlexDiv>
                </>
            )}
        </Container>
    );
}

export default observer(AwarePhishCampaignDetail);
